/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * TextBlockEditor — full-featured Text Block configuration panel.
 * 4-tab layout: Content, Style, Layout, Effects — covering content type,
 * typography, color, alignment, spacing, background/border, list, quote,
 * links, animation, visibility, and SEO.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { useTheme } from '../../../theme/ThemeContext';

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_TEXT_BLOCK_CONFIG = {
  // Content
  blockType: 'paragraph',
  text: '<p>Your content goes here.</p>',
  htmlTag: 'p',

  // Typography
  fontFamily: '',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: 16,
  responsiveFontSize: { mobile: 14, tablet: 15 },
  lineHeight: 1.7,
  letterSpacing: 0,
  textTransform: 'none',
  textDecoration: 'none',

  // Color & fill
  textColor: '#374151',
  textColorDark: '',
  highlightVisible: false,
  highlightColor: '#fde68a',
  gradientText: false,
  gradientFrom: '#7c3aed',
  gradientTo: '#2563eb',
  gradientAngle: 135,

  // Alignment
  textAlign: 'left',
  textAlignMobile: 'left',
  direction: 'ltr',

  // Spacing & layout
  marginTop: 0,
  marginBottom: 0,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  maxWidth: 'full',
  horizontalAlign: 'left',

  // Background & border
  bgVisible: false,
  bgColor: '#f8fafc',
  bgOpacity: 100,
  borderVisible: false,
  borderColor: '#e2e8f0',
  borderWidth: 1,
  borderStyle: 'solid',
  borderRadius: 0,
  borderSide: 'all',

  // List options
  listStyle: 'bullet',
  listItems: ['First item', 'Second item', 'Third item'],
  listSpacing: 'normal',
  listIconColor: '#7c3aed',

  // Quote options
  quoteStyle: 'left-border',
  quoteAccentColor: '#7c3aed',
  quoteAuthor: '',
  quoteAuthorVisible: false,
  quoteAuthorRole: '',

  // Link settings
  linkColor: '#2563eb',
  linkHoverColor: '#1d4ed8',
  linkUnderline: true,

  // Animation
  entranceAnimation: 'none',
  animationDelay: 0,
  animationDuration: 600,
  animationTrigger: 'on-load',

  // Visibility
  visibleOnDesktop: true,
  visibleOnTablet: true,
  visibleOnMobile: true,
  conditionalVisibility: false,
  conditionTag: '',

  // SEO & accessibility
  ariaLabel: '',
  role: '',
  id: '',
  customClassName: '',
};

// ─── Presets ──────────────────────────────────────────────────────────────────

const CONTENT_KEYS = ['text', 'listItems', 'quoteAuthor', 'quoteAuthorRole'];

const PRESETS = [
  {
    key: 'hero_headline',
    label: 'Hero Headline',
    icon: 'bi-type-h1',
    description: 'Large, bold, centered — for top-of-page impact',
    config: { blockType: 'heading', htmlTag: 'h1', fontWeight: 800, fontSize: 52, responsiveFontSize: { mobile: 32, tablet: 42 }, lineHeight: 1.15, letterSpacing: -0.02, textAlign: 'center', textAlignMobile: 'center', textColor: '#0f172a', textTransform: 'none', marginBottom: 16 },
  },
  {
    key: 'section_heading',
    label: 'Section Heading',
    icon: 'bi-type-h2',
    description: 'Medium size, left-aligned section title',
    config: { blockType: 'heading', htmlTag: 'h2', fontWeight: 700, fontSize: 32, responsiveFontSize: { mobile: 24, tablet: 28 }, lineHeight: 1.3, letterSpacing: -0.01, textAlign: 'left', textColor: '#1e293b', marginBottom: 12 },
  },
  {
    key: 'body_paragraph',
    label: 'Body Paragraph',
    icon: 'bi-paragraph',
    description: 'Regular weight with comfortable reading line-height',
    config: { blockType: 'paragraph', htmlTag: 'p', fontWeight: 400, fontSize: 16, responsiveFontSize: { mobile: 15, tablet: 16 }, lineHeight: 1.75, letterSpacing: 0, textAlign: 'left', textColor: '#374151' },
  },
  {
    key: 'pull_quote',
    label: 'Pull Quote',
    icon: 'bi-quote',
    description: 'Blockquote with left accent border',
    config: { blockType: 'quote', htmlTag: 'blockquote', fontWeight: 400, fontStyle: 'italic', fontSize: 18, responsiveFontSize: { mobile: 16, tablet: 17 }, lineHeight: 1.65, textColor: '#374151', quoteStyle: 'left-border', quoteAuthorVisible: true, paddingLeft: 24 },
  },
  {
    key: 'feature_label',
    label: 'Feature Label',
    icon: 'bi-tag',
    description: 'Small caps, spaced tracking, accent color',
    config: { blockType: 'label', htmlTag: 'span', fontWeight: 700, fontSize: 12, letterSpacing: 0.1, textTransform: 'uppercase', textColor: '#7c3aed', lineHeight: 1.4, marginBottom: 8 },
  },
  {
    key: 'numbered_list',
    label: 'Numbered List',
    icon: 'bi-list-ol',
    description: 'Ordered list with styled numbers',
    config: { blockType: 'list', htmlTag: 'ol', listStyle: 'numbered', listSpacing: 'normal', fontWeight: 400, fontSize: 16, lineHeight: 1.6, textColor: '#374151' },
  },
  {
    key: 'checklist',
    label: 'Checklist',
    icon: 'bi-check-square',
    description: 'Visual checklist with check icons',
    config: { blockType: 'list', htmlTag: 'ul', listStyle: 'check', listSpacing: 'normal', listIconColor: '#10b981', fontWeight: 400, fontSize: 16, lineHeight: 1.6, textColor: '#374151' },
  },
];

// ─── Google Fonts list ────────────────────────────────────────────────────────

const GOOGLE_FONTS = [
  'Albert Sans', 'Anton', 'Archivo', 'Barlow', 'Barlow Condensed', 'Bebas Neue',
  'Cabin', 'Caveat', 'Chakra Petch', 'Comfortaa', 'Cormorant Garamond',
  'Crimson Text', 'Dancing Script', 'DM Mono', 'DM Sans', 'DM Serif Display',
  'EB Garamond', 'Exo 2', 'Figtree', 'Fira Code', 'Fira Sans', 'Fraunces',
  'Great Vibes', 'IBM Plex Mono', 'IBM Plex Sans', 'Indie Flower', 'Inter',
  'JetBrains Mono', 'Josefin Sans', 'Jost', 'Kanit', 'Lato', 'Lexend',
  'Libre Baskerville', 'Lora', 'Manrope', 'Merriweather', 'Montserrat',
  'Mulish', 'Noto Sans', 'Noto Serif', 'Nunito', 'Open Sans', 'Oswald',
  'Outfit', 'Pacifico', 'Playfair Display', 'Plus Jakarta Sans', 'Poppins',
  'Quicksand', 'Raleway', 'Righteous', 'Roboto', 'Roboto Mono', 'Rubik',
  'Russo One', 'Sacramento', 'Satisfy', 'Sora', 'Source Code Pro',
  'Source Sans 3', 'Source Serif 4', 'Space Grotesk', 'Space Mono',
  'Spectral', 'Syne', 'Teko', 'Ubuntu', 'Urbanist', 'Work Sans',
].sort((a, b) => a.localeCompare(b));

// ─── Primitive controls ───────────────────────────────────────────────────────

function Toggle({ value, onChange }) {
  const { theme } = useTheme();
  return (
    <div
      role="switch" aria-checked={value} onClick={() => onChange(!value)}
      style={{ width: 36, height: 20, borderRadius: 10, background: value ? theme.primary : '#cbd5e1', position: 'relative', cursor: 'pointer', transition: 'background 0.18s', flexShrink: 0 }}
    >
      <div style={{ position: 'absolute', top: 2, left: value ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
    </div>
  );
}

function Seg({ value, onChange, options }) {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0', width: '100%' }}>
      {options.map((opt, i) => {
        const active = value === opt.value;
        return (
          <button key={opt.value} type="button" title={opt.label} onClick={() => onChange(opt.value)}
            style={{ flex: 1, padding: '5px 4px', border: 'none', borderLeft: i > 0 ? '1px solid #e2e8f0' : 'none', background: active ? theme.primary : '#f8fafc', color: active ? '#fff' : '#64748b', fontSize: 11, fontWeight: active ? 600 : 400, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, transition: 'background 0.14s, color 0.14s' }}
          >
            {opt.icon && <i className={`bi ${opt.icon}`} style={{ fontSize: 11 }} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ColorField({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)}
        style={{ width: 32, height: 28, padding: 2, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }} />
      <input className="form-control form-control-sm" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder="#000000" style={{ fontFamily: 'monospace', fontSize: 12 }} />
    </div>
  );
}

function NumInput({ value, onChange, min = 0, max = 999, step = 1, unit = '' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input type="number" className="form-control form-control-sm" value={value ?? 0}
        onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step}
        style={{ fontSize: 12, textAlign: 'center', width: 70 }} />
      {unit && <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{unit}</span>}
    </div>
  );
}

function RangeSlider({ value, onChange, min = 0, max = 100, step = 1, unit = '' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input type="range" min={min} max={max} step={step} value={value ?? 0}
        onChange={e => onChange(Number(e.target.value))}
        style={{ flex: 1, accentColor: 'var(--bs-primary)' }} />
      <span style={{ fontSize: 11, color: '#64748b', minWidth: 30, textAlign: 'right' }}>{value}{unit}</span>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {hint && (
          <span title={hint} style={{ cursor: 'help', color: '#94a3b8', fontSize: 10 }}>
            <i className="bi bi-info-circle" />
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function InlineRow({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {hint && <span title={hint} style={{ cursor: 'help', color: '#94a3b8', fontSize: 10 }}><i className="bi bi-info-circle" /></span>}
      </span>
      {children}
    </div>
  );
}

function SectionDivider({ label }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', margin: '12px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
      {label}
      <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
    </div>
  );
}

function SubPanel({ children }) {
  return (
    <div style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 6, border: '1px solid #f1f5f9', marginBottom: 8 }}>
      {children}
    </div>
  );
}

function Accordion({ title, icon, badge, open, onToggle, children }) {
  const { theme } = useTheme();
  return (
    <div style={{ marginBottom: 2 }}>
      <button type="button" onClick={onToggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 7, padding: '9px 10px', border: `1px solid ${open ? theme.primary + '30' : '#e2e8f0'}`, borderRadius: open ? '6px 6px 0 0' : 6, background: open ? theme.primary + '08' : '#fafafa', cursor: 'pointer', textAlign: 'left' }}
      >
        {icon && <i className={`bi ${icon}`} style={{ fontSize: 13, color: open ? theme.primary : '#64748b', flexShrink: 0 }} />}
        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: open ? theme.primary : '#374151' }}>{title}</span>
        {badge && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: theme.primary + '18', color: theme.primary }}>{badge}</span>}
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#94a3b8' }} />
      </button>
      {open && (
        <div style={{ padding: '12px 10px 4px', border: `1px solid ${theme.primary}18`, borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff', marginBottom: 2 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Font Picker ──────────────────────────────────────────────────────────────

function FontPicker({ value, onChange }) {
  const { theme } = useTheme();
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const containerRef        = useRef(null);
  const searchRef           = useRef(null);

  const filtered = GOOGLE_FONTS.filter(f => f.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false); setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const select = (font) => { onChange(font); setOpen(false); setSearch(''); };
  const clear  = ()      => { onChange('');   setOpen(false); setSearch(''); };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, gap: 6, color: value ? '#1e293b' : '#9ca3af', outline: open ? `2px solid ${theme.primary}40` : 'none' }}
      >
        <span style={{ fontFamily: value ? `"${value}", sans-serif` : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'Default (inherit)'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {value && (
            <span role="button" onClick={e => { e.stopPropagation(); clear(); }}
              style={{ color: '#94a3b8', lineHeight: 1, padding: '0 2px', cursor: 'pointer' }} title="Clear font">
              <i className="bi bi-x" style={{ fontSize: 13 }} />
            </span>
          )}
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#94a3b8' }} />
        </div>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,0.14)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ position: 'relative' }}>
              <i className="bi bi-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#94a3b8', pointerEvents: 'none' }} />
              <input ref={searchRef} className="form-control form-control-sm" placeholder="Search fonts…" value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setSearch(''); } if (e.key === 'Enter' && filtered.length > 0) select(filtered[0]); }}
                style={{ paddingLeft: 26, fontSize: 12 }} />
            </div>
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            <button type="button" onClick={clear}
              style={{ width: '100%', padding: '7px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', background: !value ? `${theme.primary}10` : '#fff', textAlign: 'left', fontSize: 11, color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="bi bi-x-circle" style={{ fontSize: 11 }} /> Default (no custom font)
            </button>
            {filtered.length === 0 ? (
              <div style={{ padding: '14px 12px', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>No fonts match "<strong>{search}</strong>"</div>
            ) : filtered.map(font => {
              const active = value === font;
              return (
                <button key={font} type="button" onClick={() => select(font)}
                  style={{ width: '100%', padding: '7px 12px', border: 'none', borderBottom: '1px solid #f8fafc', background: active ? `${theme.primary}10` : '#fff', textAlign: 'left', fontSize: 13, fontFamily: `"${font}", sans-serif`, color: active ? theme.primary : '#374151', fontWeight: active ? 700 : 400, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#fff'; }}
                >
                  {font}
                  {active && <i className="bi bi-check2" style={{ fontSize: 12, color: theme.primary }} />}
                </button>
              );
            })}
          </div>
          <div style={{ padding: '6px 10px', borderTop: '1px solid #f1f5f9', fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
            {filtered.length} font{filtered.length !== 1 ? 's' : ''} • loads automatically from Google Fonts
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Font weight selector ─────────────────────────────────────────────────────

const WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];
const WEIGHT_LABEL = { 100: 'Thin', 200: 'ExLight', 300: 'Light', 400: 'Regular', 500: 'Medium', 600: 'SemiBold', 700: 'Bold', 800: 'ExBold', 900: 'Black' };

function WeightSelector({ value, onChange }) {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
      {WEIGHTS.map(w => {
        const active = value === w;
        return (
          <button key={w} type="button" onClick={() => onChange(w)} title={WEIGHT_LABEL[w]}
            style={{ padding: '5px 4px', border: `1px solid ${active ? theme.primary : '#e2e8f0'}`, borderRadius: 5, background: active ? `${theme.primary}10` : '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <span style={{ fontWeight: w, fontSize: 14, color: active ? theme.primary : '#374151', lineHeight: 1 }}>Aa</span>
            <span style={{ fontSize: 9, color: active ? theme.primary : '#94a3b8' }}>{w}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── List items editor ────────────────────────────────────────────────────────

function ListItemsEditor({ items, onChange }) {
  const addItem = () => onChange([...items, '']);
  const updateItem = (i, val) => { const n = [...items]; n[i] = val; onChange(n); };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const moveUp = (i) => { if (i === 0) return; const n = [...items]; [n[i-1], n[i]] = [n[i], n[i-1]]; onChange(n); };
  const moveDown = (i) => { if (i === items.length - 1) return; const n = [...items]; [n[i], n[i+1]] = [n[i+1], n[i]]; onChange(n); };

  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 5, alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <button type="button" onClick={() => moveUp(i)} disabled={i === 0}
              style={{ padding: '1px 5px', border: '1px solid #e2e8f0', borderRadius: 3, background: '#fff', fontSize: 9, cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1 }}>▲</button>
            <button type="button" onClick={() => moveDown(i)} disabled={i === items.length - 1}
              style={{ padding: '1px 5px', border: '1px solid #e2e8f0', borderRadius: 3, background: '#fff', fontSize: 9, cursor: i === items.length - 1 ? 'default' : 'pointer', opacity: i === items.length - 1 ? 0.3 : 1 }}>▼</button>
          </div>
          <input className="form-control form-control-sm" value={item} onChange={e => updateItem(i, e.target.value)}
            placeholder={`Item ${i + 1}`} style={{ flex: 1, fontSize: 12 }} />
          <button type="button" onClick={() => removeItem(i)}
            style={{ padding: '4px 7px', border: '1px solid #fecaca', borderRadius: 4, background: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>
            <i className="bi bi-trash" />
          </button>
        </div>
      ))}
      <button type="button" onClick={addItem}
        style={{ width: '100%', padding: '6px', border: '1px dashed #d1d5db', borderRadius: 6, background: '#f9fafb', color: '#6b7280', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        <i className="bi bi-plus" /> Add Item
      </button>
    </div>
  );
}

// ─── Editor contrast background ──────────────────────────────────────────────
// Ensures typed text is always visible regardless of the configured text color.
function getEditorBg(hexColor) {
  if (!hexColor || !hexColor.startsWith('#') || hexColor.length < 7) return '#ffffff';
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#1e293b' : '#ffffff';
}

// ─── Rich text editor (TipTap) ────────────────────────────────────────────────

function ToolbarBtn({ children, active, onClick, title }) {
  const { theme } = useTheme();
  return (
    <button type="button" onClick={onClick} title={title}
      style={{ padding: '4px 7px', border: 'none', borderRadius: 4, background: active ? `${theme.primary}18` : 'transparent', color: active ? theme.primary : '#475569', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', lineHeight: 1 }}>
      {children}
    </button>
  );
}

function RichTextEditor({ value, onChange, textColor, textAlign, fontFamily, fontSize }) {
  const { theme } = useTheme();
  const [hasSelection, setHasSelection] = useState(false);
  const [activeMarks, setActiveMarks] = useState({});

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Highlight.configure({ multicolor: false }),
      TextStyle,
    ],
    content: value || '<p></p>',
    onUpdate:         ({ editor }) => onChange(editor.getHTML()),
    onSelectionUpdate:({ editor }) => {
      const { from, to } = editor.state.selection;
      const sel = from !== to;
      setHasSelection(sel);
      if (sel) {
        setActiveMarks({
          bold:       editor.isActive('bold'),
          italic:     editor.isActive('italic'),
          underline:  editor.isActive('underline'),
          strike:     editor.isActive('strike'),
          highlight:  editor.isActive('highlight'),
          link:       editor.isActive('link'),
        });
      }
    },
    onTransaction: ({ editor }) => {
      // Keep active mark state in sync when selection stays but marks change
      const { from, to } = editor.state.selection;
      if (from !== to) {
        setActiveMarks({
          bold:       editor.isActive('bold'),
          italic:     editor.isActive('italic'),
          underline:  editor.isActive('underline'),
          strike:     editor.isActive('strike'),
          highlight:  editor.isActive('highlight'),
          link:       editor.isActive('link'),
        });
      }
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL:', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  // Sync external value changes (e.g. preset applied)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) {
      editor.commands.setContent(value, false);
    }
  }, [editor, value]);

  // Contextual actions that fire on the current selection
  const selectionActions = editor ? [
    { label: <strong style={{ fontSize: 13 }}>B</strong>, active: activeMarks.bold,      run: () => editor.chain().focus().toggleBold().run(),      title: 'Bold (⌘B)' },
    { label: <em style={{ fontSize: 13 }}>I</em>,         active: activeMarks.italic,    run: () => editor.chain().focus().toggleItalic().run(),    title: 'Italic (⌘I)' },
    { label: <u style={{ fontSize: 13 }}>U</u>,           active: activeMarks.underline, run: () => editor.chain().focus().toggleUnderline().run(), title: 'Underline' },
    { label: <s style={{ fontSize: 13 }}>S</s>,           active: activeMarks.strike,    run: () => editor.chain().focus().toggleStrike().run(),    title: 'Strikethrough' },
    { label: <i className="bi bi-highlighter" />,          active: activeMarks.highlight, run: () => editor.chain().focus().toggleHighlight().run(), title: 'Highlight' },
    { label: <i className="bi bi-link-45deg" />,           active: activeMarks.link,      run: setLink,                                              title: 'Link' },
    { label: <i className="bi bi-eraser" />,               active: false,                 run: () => editor.chain().focus().unsetAllMarks().run(),   title: 'Clear formatting' },
  ] : [];

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>

      {/* Static formatting toolbar — always visible */}
      <div style={{ display: 'flex', gap: 2, padding: '5px 8px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', flexWrap: 'wrap' }}>
        <ToolbarBtn active={editor?.isActive('bold')}      onClick={() => editor?.chain().focus().toggleBold().run()}      title="Bold (⌘B)"><i className="bi bi-type-bold" /></ToolbarBtn>
        <ToolbarBtn active={editor?.isActive('italic')}    onClick={() => editor?.chain().focus().toggleItalic().run()}    title="Italic (⌘I)"><i className="bi bi-type-italic" /></ToolbarBtn>
        <ToolbarBtn active={editor?.isActive('underline')} onClick={() => editor?.chain().focus().toggleUnderline().run()} title="Underline (⌘U)"><i className="bi bi-type-underline" /></ToolbarBtn>
        <ToolbarBtn active={editor?.isActive('strike')}    onClick={() => editor?.chain().focus().toggleStrike().run()}    title="Strikethrough"><i className="bi bi-type-strikethrough" /></ToolbarBtn>
        <div style={{ width: 1, height: 18, background: '#e2e8f0', margin: '0 2px', alignSelf: 'center' }} />
        <ToolbarBtn active={editor?.isActive('highlight')} onClick={() => editor?.chain().focus().toggleHighlight().run()} title="Highlight"><i className="bi bi-highlighter" /></ToolbarBtn>
        <ToolbarBtn active={editor?.isActive('link')}      onClick={setLink}                                               title="Link (⌘K)"><i className="bi bi-link-45deg" /></ToolbarBtn>
        <div style={{ marginLeft: 'auto' }}>
          <ToolbarBtn onClick={() => editor?.chain().focus().unsetAllMarks().run()} title="Clear formatting"><i className="bi bi-eraser" /></ToolbarBtn>
        </div>
      </div>

      {/* Contextual toolbar — slides in when text is selected */}
      {hasSelection && (
        <div style={{ display: 'flex', gap: 2, padding: '4px 8px', background: '#1e293b', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, color: '#64748b', marginRight: 2, letterSpacing: '0.06em', fontWeight: 600 }}>SELECTION</span>
          {selectionActions.map((action, i) => (
            <button key={i} type="button" onClick={action.run} title={action.title}
              style={{ padding: '3px 8px', border: 'none', borderRadius: 4, background: action.active ? theme.primary : 'transparent', color: action.active ? '#fff' : '#94a3b8', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', lineHeight: 1 }}>
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Editor content */}
      <div style={{ padding: '8px 10px', minHeight: 90, fontFamily: fontFamily || 'inherit', color: textColor || '#374151', fontSize: Math.min(fontSize || 16, 16), textAlign: textAlign || 'left', lineHeight: 1.6, background: getEditorBg(textColor) }}
        className="tiptap-editor-wrap">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

// ─── Tab definitions ─────────────────────────────────────────────────────────

const TABS = [
  { key: 'content', label: 'Content',  icon: 'bi-file-text' },
  { key: 'style',   label: 'Style',    icon: 'bi-palette' },
  { key: 'layout',  label: 'Layout',   icon: 'bi-layout-text-window' },
  { key: 'effects', label: 'Effects',  icon: 'bi-magic' },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function TextBlockEditor({ section, dispatch }) {
  const { theme } = useTheme();
  const cfg = { ...DEFAULT_TEXT_BLOCK_CONFIG, ...section.section_config };

  const [activeTab,     setActiveTab]    = useState('content');
  const [showPresets,   setShowPresets]  = useState(false);
  const [showResponsiveFontSize, setShowResponsiveFontSize] = useState(false);
  const [copySuccess,   setCopySuccess]  = useState(false);

  const set = useCallback((patch) => {
    dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch });
  }, [dispatch, section._clientId]);

  const updateLayout = useCallback((patch) => {
    dispatch({ type: 'UPDATE_LAYOUT_CONFIG', clientId: section._clientId, patch });
  }, [dispatch, section._clientId]);

  // Preset apply — smart merge: structural fields from preset, content from current
  const applyPreset = (preset) => {
    const merged = { ...preset.config };
    CONTENT_KEYS.forEach(k => { if (cfg[k] !== undefined) merged[k] = cfg[k]; });
    set(merged);
    setShowPresets(false);
  };

  const resetToDefault = () => set(DEFAULT_TEXT_BLOCK_CONFIG);

  // Copy/Paste styles
  const copyStyles = async () => {
    const styles = { ...cfg };
    CONTENT_KEYS.forEach(k => delete styles[k]);
    await navigator.clipboard.writeText(JSON.stringify(styles, null, 2)).catch(() => {});
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 1800);
  };

  const pasteStyles = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const styles = JSON.parse(raw);
      const safe = { ...styles };
      CONTENT_KEYS.forEach(k => delete safe[k]);
      set(safe);
    } catch {
      // invalid clipboard content — ignore silently
    }
  };

  const isList  = cfg.blockType === 'list';
  const isQuote = cfg.blockType === 'quote';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <style>{`
        .tiptap-editor-wrap .ProseMirror { outline: none; }
        .tiptap-editor-wrap .ProseMirror p { margin: 0 0 0.5em; }
        .tiptap-editor-wrap .ProseMirror p:last-child { margin-bottom: 0; }
        .tiptap-editor-wrap .ProseMirror a { color: ${cfg.linkColor}; text-decoration: ${cfg.linkUnderline ? 'underline' : 'none'}; }
        .tiptap-editor-wrap .ProseMirror mark { background: ${cfg.highlightColor}; border-radius: 2px; padding: 0 2px; }
      `}</style>

      {/* ── Top bar: Presets / Copy / Paste / Reset ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, position: 'relative' }}>
        <button type="button" onClick={() => setShowPresets(p => !p)}
          style={{ flex: 1, padding: '6px 10px', border: `1px solid ${theme.primary}`, borderRadius: 6, background: showPresets ? theme.primary : '#fff', color: showPresets ? '#fff' : theme.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          <i className="bi bi-stars" /> Presets <i className={`bi bi-chevron-${showPresets ? 'up' : 'down'}`} style={{ fontSize: 10 }} />
        </button>
        <button type="button" onClick={copyStyles} title="Copy all style settings (not text content)"
          style={{ padding: '6px 9px', border: '1px solid #e2e8f0', borderRadius: 6, background: copySuccess ? '#f0fdf4' : '#fff', color: copySuccess ? '#166534' : '#64748b', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className={`bi ${copySuccess ? 'bi-check2' : 'bi-clipboard'}`} /> {copySuccess ? 'Copied' : 'Copy'}
        </button>
        <button type="button" onClick={pasteStyles} title="Paste style settings from clipboard"
          style={{ padding: '6px 9px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#64748b', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-clipboard-check" /> Paste
        </button>
        <button type="button" onClick={resetToDefault} title="Reset all fields to defaults"
          style={{ padding: '6px 9px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#64748b', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <i className="bi bi-arrow-counterclockwise" />
        </button>

        {showPresets && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', overflow: 'hidden', marginTop: 4 }}>
            {PRESETS.map((preset, i) => (
              <button key={preset.key} type="button" onClick={() => applyPreset(preset)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: 'none', background: '#fff', cursor: 'pointer', textAlign: 'left', borderBottom: i < PRESETS.length - 1 ? '1px solid #f1f5f9' : 'none' }}
                onMouseEnter={e => (e.currentTarget.style.background = theme.primary + '0f')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                <i className={`bi ${preset.icon}`} style={{ fontSize: 17, color: theme.primary, width: 20, textAlign: 'center', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{preset.label}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{preset.description}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Tab bar ── */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0',
        marginBottom: 14, overflowX: 'auto', flexShrink: 0,
      }}>
        {TABS.map(tab => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '9px 14px',
            border: 'none',
            borderBottom: activeTab === tab.key ? `2px solid ${theme.primary}` : '2px solid transparent',
            marginBottom: -2,
            background: 'transparent',
            color: activeTab === tab.key ? theme.primary : '#64748b',
            fontWeight: activeTab === tab.key ? 700 : 400,
            fontSize: 11,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'color 0.15s',
          }}>
            <i className={`bi ${tab.icon}`} style={{ fontSize: 12 }} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Content tab ── */}
      {activeTab === 'content' && (<>
        {/* CONTENT & TYPE */}
        <SectionDivider label="CONTENT & TYPE" />

        <Row label="Block Type">
          <select className="form-select form-select-sm" value={cfg.blockType} onChange={e => set({ blockType: e.target.value })} style={{ fontSize: 12 }}>
            <option value="paragraph">Paragraph</option>
            <option value="heading">Heading</option>
            <option value="subheading">Subheading</option>
            <option value="label">Label</option>
            <option value="caption">Caption</option>
            <option value="quote">Quote / Blockquote</option>
            <option value="list">List</option>
          </select>
        </Row>

        <Row label="HTML Tag">
          <select className="form-select form-select-sm" value={cfg.htmlTag} onChange={e => set({ htmlTag: e.target.value })} style={{ fontSize: 12 }}>
            <option value="p">p — paragraph</option>
            <option value="h1">h1 — heading 1</option>
            <option value="h2">h2 — heading 2</option>
            <option value="h3">h3 — heading 3</option>
            <option value="h4">h4 — heading 4</option>
            <option value="span">span — inline</option>
            <option value="blockquote">blockquote</option>
            <option value="label">label</option>
            <option value="ol">ol — ordered list</option>
            <option value="ul">ul — unordered list</option>
          </select>
        </Row>

        {/* Rich text editor — shown for non-list types */}
        {!isList && (
          <Row label="Content">
            <RichTextEditor
              value={cfg.text}
              onChange={v => set({ text: v })}
              textColor={cfg.textColor}
              textAlign={cfg.textAlign}
              fontFamily={cfg.fontFamily}
              fontSize={cfg.fontSize}
            />
          </Row>
        )}

        {/* List items editor — shown for list type */}
        {isList && (
          <Row label="List Items">
            <ListItemsEditor items={cfg.listItems} onChange={v => set({ listItems: v })} />
          </Row>
        )}

        {/* LIST OPTIONS (conditional) */}
        {isList && (<>
          <SectionDivider label="LIST OPTIONS" />

          <Row label="List Style">
            <Seg value={cfg.listStyle} onChange={v => set({ listStyle: v })} options={[
              { value: 'bullet',   label: '• Bullet' },
              { value: 'numbered', label: '1. Number' },
              { value: 'check',    label: '✓ Check' },
              { value: 'arrow',    label: '→ Arrow' },
            ]} />
          </Row>

          <Row label="Item Spacing">
            <Seg value={cfg.listSpacing} onChange={v => set({ listSpacing: v })} options={[
              { value: 'tight',  label: 'Tight' },
              { value: 'normal', label: 'Normal' },
              { value: 'loose',  label: 'Loose' },
            ]} />
          </Row>

          {(cfg.listStyle === 'check' || cfg.listStyle === 'arrow') && (
            <Row label="Icon Color">
              <ColorField value={cfg.listIconColor} onChange={v => set({ listIconColor: v })} />
            </Row>
          )}
        </>)}

        {/* QUOTE OPTIONS (conditional) */}
        {isQuote && (<>
          <SectionDivider label="QUOTE OPTIONS" />

          <Row label="Quote Style">
            <select className="form-select form-select-sm" value={cfg.quoteStyle} onChange={e => set({ quoteStyle: e.target.value })} style={{ fontSize: 12 }}>
              <option value="left-border">Left accent border</option>
              <option value="large-quote-mark">Large quote mark</option>
              <option value="centered">Centered</option>
              <option value="card">Card with background</option>
            </select>
          </Row>

          <Row label="Accent Color">
            <ColorField value={cfg.quoteAccentColor} onChange={v => set({ quoteAccentColor: v })} />
          </Row>

          <InlineRow label="Show Author">
            <Toggle value={cfg.quoteAuthorVisible} onChange={v => set({ quoteAuthorVisible: v })} />
          </InlineRow>

          {cfg.quoteAuthorVisible && (
            <SubPanel>
              <Row label="Author Name">
                <input className="form-control form-control-sm" value={cfg.quoteAuthor} onChange={e => set({ quoteAuthor: e.target.value })} placeholder="Jane Smith" style={{ fontSize: 12 }} />
              </Row>
              <Row label="Author Role">
                <input className="form-control form-control-sm" value={cfg.quoteAuthorRole} onChange={e => set({ quoteAuthorRole: e.target.value })} placeholder="CEO, Acme Corp" style={{ fontSize: 12 }} />
              </Row>
            </SubPanel>
          )}
        </>)}
      </>)}

      {/* ── Style tab ── */}
      {activeTab === 'style' && (<>
        {/* TYPOGRAPHY */}
        <SectionDivider label="TYPOGRAPHY" />

        <Row label="Font Family">
          <FontPicker value={cfg.fontFamily} onChange={v => set({ fontFamily: v })} />
        </Row>

        <Row label="Font Weight">
          <WeightSelector value={cfg.fontWeight} onChange={v => set({ fontWeight: v })} />
        </Row>

        <Row label="Font Style">
          <Seg value={cfg.fontStyle} onChange={v => set({ fontStyle: v })} options={[
            { value: 'normal', label: 'Normal' },
            { value: 'italic', label: 'Italic' },
          ]} />
        </Row>

        <Row label={`Font Size — ${cfg.fontSize}px`}>
          <RangeSlider value={cfg.fontSize} onChange={v => set({ fontSize: v })} min={8} max={120} unit="px" />
        </Row>

        {/* Responsive font size overrides */}
        <div style={{ marginBottom: 10 }}>
          <button type="button" onClick={() => setShowResponsiveFontSize(v => !v)}
            style={{ fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
            <i className={`bi bi-chevron-${showResponsiveFontSize ? 'up' : 'down'}`} style={{ fontSize: 10 }} />
            Responsive font size overrides
          </button>
          {showResponsiveFontSize && (
            <SubPanel>
              <Row label={`Mobile — ${cfg.responsiveFontSize?.mobile ?? cfg.fontSize}px`}>
                <RangeSlider value={cfg.responsiveFontSize?.mobile ?? cfg.fontSize} onChange={v => set({ responsiveFontSize: { ...cfg.responsiveFontSize, mobile: v } })} min={8} max={80} unit="px" />
              </Row>
              <Row label={`Tablet — ${cfg.responsiveFontSize?.tablet ?? cfg.fontSize}px`}>
                <RangeSlider value={cfg.responsiveFontSize?.tablet ?? cfg.fontSize} onChange={v => set({ responsiveFontSize: { ...cfg.responsiveFontSize, tablet: v } })} min={8} max={100} unit="px" />
              </Row>
            </SubPanel>
          )}
        </div>

        <Row label={`Line Height — ${cfg.lineHeight}`}>
          <RangeSlider value={cfg.lineHeight} onChange={v => set({ lineHeight: v })} min={0.8} max={3} step={0.05} />
        </Row>

        <Row label={`Letter Spacing — ${cfg.letterSpacing}em`}>
          <RangeSlider value={cfg.letterSpacing} onChange={v => set({ letterSpacing: v })} min={-0.1} max={0.5} step={0.01} />
        </Row>

        <Row label="Text Transform">
          <Seg value={cfg.textTransform} onChange={v => set({ textTransform: v })} options={[
            { value: 'none',       label: 'None' },
            { value: 'uppercase',  label: 'UPPER' },
            { value: 'lowercase',  label: 'lower' },
            { value: 'capitalize', label: 'Caps' },
          ]} />
        </Row>

        <Row label="Text Decoration">
          <Seg value={cfg.textDecoration} onChange={v => set({ textDecoration: v })} options={[
            { value: 'none',         label: 'None' },
            { value: 'underline',    label: 'Under' },
            { value: 'line-through', label: 'Strike' },
          ]} />
        </Row>

        {/* COLOR & FILL */}
        <SectionDivider label="COLOR & FILL" />

        <Row label="Text Color">
          <ColorField value={cfg.textColor} onChange={v => set({ textColor: v })} />
        </Row>

        <InlineRow label="Gradient Text" hint="Applies a gradient fill to the text itself">
          <Toggle value={cfg.gradientText} onChange={v => set({ gradientText: v })} />
        </InlineRow>

        {cfg.gradientText && (
          <SubPanel>
            <Row label="From Color"><ColorField value={cfg.gradientFrom} onChange={v => set({ gradientFrom: v })} /></Row>
            <Row label="To Color"><ColorField value={cfg.gradientTo} onChange={v => set({ gradientTo: v })} /></Row>
            <Row label={`Angle — ${cfg.gradientAngle}°`}>
              <RangeSlider value={cfg.gradientAngle} onChange={v => set({ gradientAngle: v })} min={0} max={360} unit="°" />
            </Row>
          </SubPanel>
        )}

        <InlineRow label="Text Highlight">
          <Toggle value={cfg.highlightVisible} onChange={v => set({ highlightVisible: v })} />
        </InlineRow>
        {cfg.highlightVisible && (
          <Row label="Highlight Color">
            <ColorField value={cfg.highlightColor} onChange={v => set({ highlightColor: v })} />
          </Row>
        )}

        {/* LINK SETTINGS */}
        <SectionDivider label="LINK SETTINGS" />

        <Row label="Link Color">
          <ColorField value={cfg.linkColor} onChange={v => set({ linkColor: v })} />
        </Row>
        <Row label="Link Hover Color">
          <ColorField value={cfg.linkHoverColor} onChange={v => set({ linkHoverColor: v })} />
        </Row>
        <InlineRow label="Underline Links">
          <Toggle value={cfg.linkUnderline} onChange={v => set({ linkUnderline: v })} />
        </InlineRow>
      </>)}

      {/* ── Layout tab ── */}
      {activeTab === 'layout' && (<>
        {/* ALIGNMENT */}
        <SectionDivider label="ALIGNMENT" />

        <Row label="Text Align">
          <Seg value={cfg.textAlign} onChange={v => set({ textAlign: v })} options={[
            { value: 'left',    icon: 'bi-text-left',   label: 'Left' },
            { value: 'center',  icon: 'bi-text-center', label: 'Center' },
            { value: 'right',   icon: 'bi-text-right',  label: 'Right' },
            { value: 'justify', icon: 'bi-justify',     label: 'Justify' },
          ]} />
        </Row>

        <Row label="Mobile Text Align">
          <Seg value={cfg.textAlignMobile} onChange={v => set({ textAlignMobile: v })} options={[
            { value: 'left',   icon: 'bi-text-left',   label: 'Left' },
            { value: 'center', icon: 'bi-text-center', label: 'Center' },
            { value: 'right',  icon: 'bi-text-right',  label: 'Right' },
          ]} />
        </Row>

        <Row label="Block Align" hint="Aligns the block itself inside its container">
          <Seg value={cfg.horizontalAlign} onChange={v => set({ horizontalAlign: v })} options={[
            { value: 'left',   label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right',  label: 'Right' },
          ]} />
        </Row>

        <Row label="Direction">
          <Seg value={cfg.direction} onChange={v => set({ direction: v })} options={[
            { value: 'ltr', label: 'LTR' },
            { value: 'rtl', label: 'RTL' },
          ]} />
        </Row>

        {/* SPACING */}
        <SectionDivider label="MARGIN (outside)" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Row label="Top"><NumInput value={cfg.marginTop} onChange={v => set({ marginTop: v })} min={0} max={200} unit="px" /></Row>
          <Row label="Bottom"><NumInput value={cfg.marginBottom} onChange={v => set({ marginBottom: v })} min={0} max={200} unit="px" /></Row>
        </div>

        <SectionDivider label="PADDING (inside)" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Row label="Top"><NumInput value={cfg.paddingTop} onChange={v => set({ paddingTop: v })} min={0} max={200} unit="px" /></Row>
          <Row label="Bottom"><NumInput value={cfg.paddingBottom} onChange={v => set({ paddingBottom: v })} min={0} max={200} unit="px" /></Row>
          <Row label="Left"><NumInput value={cfg.paddingLeft} onChange={v => set({ paddingLeft: v })} min={0} max={200} unit="px" /></Row>
          <Row label="Right"><NumInput value={cfg.paddingRight} onChange={v => set({ paddingRight: v })} min={0} max={200} unit="px" /></Row>
        </div>

        <SectionDivider label="CONSTRAINTS" />
        <Row label="Max Width">
          <Seg value={cfg.maxWidth} onChange={v => set({ maxWidth: v })} options={[
            { value: 'full',      label: 'Full' },
            { value: 640,         label: '640px' },
            { value: 768,         label: '768px' },
            { value: 1024,        label: '1024px' },
          ]} />
        </Row>

        {/* BACKGROUND & BORDER */}
        <SectionDivider label="SECTION BACKGROUND" />
        <Row label="Color">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ColorField
              value={section.layout_config?.customBgColor || ''}
              onChange={v => updateLayout({ customBgColor: v || null })}
            />
            {section.layout_config?.customBgColor && (
              <button
                type="button"
                onClick={() => updateLayout({ customBgColor: null })}
                style={{ fontSize: 11, padding: '2px 7px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >Clear</button>
            )}
          </div>
        </Row>
        <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 8px', lineHeight: 1.4 }}>
          Fills the full width of the page section container.
        </p>

        <SectionDivider label="CONTENT BOX BACKGROUND" />
        <InlineRow label="Enable">
          <Toggle value={cfg.bgVisible} onChange={v => set({ bgVisible: v })} />
        </InlineRow>
        {cfg.bgVisible && (
          <SubPanel>
            <Row label="Color"><ColorField value={cfg.bgColor} onChange={v => set({ bgColor: v })} /></Row>
            <Row label={`Opacity — ${cfg.bgOpacity}%`}>
              <RangeSlider value={cfg.bgOpacity} onChange={v => set({ bgOpacity: v })} min={0} max={100} unit="%" />
            </Row>
          </SubPanel>
        )}

        <SectionDivider label="BORDER" />
        <InlineRow label="Border">
          <Toggle value={cfg.borderVisible} onChange={v => set({ borderVisible: v })} />
        </InlineRow>
        {cfg.borderVisible && (
          <SubPanel>
            <Row label="Color"><ColorField value={cfg.borderColor} onChange={v => set({ borderColor: v })} /></Row>
            <Row label={`Width — ${cfg.borderWidth}px`}>
              <RangeSlider value={cfg.borderWidth} onChange={v => set({ borderWidth: v })} min={1} max={12} unit="px" />
            </Row>
            <Row label="Style">
              <Seg value={cfg.borderStyle} onChange={v => set({ borderStyle: v })} options={[
                { value: 'solid',  label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
                { value: 'dotted', label: 'Dotted' },
              ]} />
            </Row>
            <Row label="Side">
              <Seg value={cfg.borderSide} onChange={v => set({ borderSide: v })} options={[
                { value: 'all',    label: 'All' },
                { value: 'left',   label: 'Left' },
                { value: 'right',  label: 'Right' },
                { value: 'bottom', label: 'Bottom' },
              ]} />
            </Row>
            <Row label={`Radius — ${cfg.borderRadius}px`}>
              <RangeSlider value={cfg.borderRadius} onChange={v => set({ borderRadius: v })} min={0} max={40} unit="px" />
            </Row>
          </SubPanel>
        )}
      </>)}

      {/* ── Effects tab ── */}
      {activeTab === 'effects' && (<>
        {/* ANIMATION */}
        <SectionDivider label="ANIMATION" />

        <Row label="Entrance Animation">
          <select className="form-select form-select-sm" value={cfg.entranceAnimation} onChange={e => set({ entranceAnimation: e.target.value })} style={{ fontSize: 12 }}>
            <option value="none">None</option>
            <option value="fade-in">Fade In</option>
            <option value="slide-up">Slide Up</option>
            <option value="slide-left">Slide from Left</option>
            <option value="slide-right">Slide from Right</option>
            <option value="zoom-in">Zoom In</option>
          </select>
        </Row>

        {cfg.entranceAnimation !== 'none' && (
          <>
            <Row label="Trigger">
              <Seg value={cfg.animationTrigger} onChange={v => set({ animationTrigger: v })} options={[
                { value: 'on-load',   label: 'On Load' },
                { value: 'on-scroll', label: 'On Scroll' },
              ]} />
            </Row>
            <Row label={`Delay — ${cfg.animationDelay}ms`}>
              <RangeSlider value={cfg.animationDelay} onChange={v => set({ animationDelay: v })} min={0} max={2000} step={50} unit="ms" />
            </Row>
            <Row label={`Duration — ${cfg.animationDuration}ms`}>
              <RangeSlider value={cfg.animationDuration} onChange={v => set({ animationDuration: v })} min={200} max={2000} step={50} unit="ms" />
            </Row>
          </>
        )}

        {/* RESPONSIVE VISIBILITY */}
        <SectionDivider label="RESPONSIVE VISIBILITY" />

        <InlineRow label="Visible on Desktop"><Toggle value={cfg.visibleOnDesktop} onChange={v => set({ visibleOnDesktop: v })} /></InlineRow>
        <InlineRow label="Visible on Tablet"> <Toggle value={cfg.visibleOnTablet}  onChange={v => set({ visibleOnTablet: v })}  /></InlineRow>
        <InlineRow label="Visible on Mobile"> <Toggle value={cfg.visibleOnMobile}  onChange={v => set({ visibleOnMobile: v })}  /></InlineRow>

        <InlineRow label="Conditional Visibility" hint="Show/hide based on a condition tag">
          <Toggle value={cfg.conditionalVisibility} onChange={v => set({ conditionalVisibility: v })} />
        </InlineRow>
        {cfg.conditionalVisibility && (
          <Row label="Condition Tag">
            <input className="form-control form-control-sm" value={cfg.conditionTag} onChange={e => set({ conditionTag: e.target.value })} placeholder="e.g. user.isLoggedIn" style={{ fontSize: 12 }} />
          </Row>
        )}
      </>)}

    </div>
  );
}
